/**
 * Click event handlers for the canvas map component
 */

import type {
  CanvasMapState,
  ExpandCollapseCallbacks,
  UndoCallbacks,
  HomeIconCallbacks,
} from '../types/interfaces';
import {
  MAP_CONSTANTS,
  DEFAULT_MAP_SIZE,
  EXPANDED_MAP_CONSTANTS,
} from '../types/constants';

/**
 * Calculate maximum expanded size based on parent element constraints
 */
function calculateMaxExpandedSize(
  parentElement?: HTMLElement,
  fallbackWidth: number = EXPANDED_MAP_CONSTANTS.DEFAULT_EXPANDED_WIDTH,
  fallbackHeight: number = EXPANDED_MAP_CONSTANTS.DEFAULT_EXPANDED_HEIGHT
): { width: number; height: number; x: number; y: number } {
  // Default expanded size when no parent constraints
  if (!parentElement) {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    return {
      width: fallbackWidth,
      height: fallbackHeight,
      x:
        (viewportWidth - fallbackWidth) /
        EXPANDED_MAP_CONSTANTS.DIVISION_FACTOR,
      y:
        (viewportHeight - fallbackHeight) /
        EXPANDED_MAP_CONSTANTS.DIVISION_FACTOR,
    };
  }

  // Get parent bounds
  const parentBounds = parentElement.getBoundingClientRect();
  const padding = EXPANDED_MAP_CONSTANTS.PADDING; // Extra padding for expanded view

  // Calculate available space within parent
  const availableWidth =
    parentBounds.width - padding * EXPANDED_MAP_CONSTANTS.DIVISION_FACTOR;
  const availableHeight =
    parentBounds.height - padding * EXPANDED_MAP_CONSTANTS.DIVISION_FACTOR;

  // Respect maximum expanded size but constrain to parent
  const constrainedWidth = Math.min(fallbackWidth, availableWidth);
  const constrainedHeight = Math.min(fallbackHeight, availableHeight);

  // Apply minimum size constraints
  const finalWidth = Math.max(
    constrainedWidth,
    EXPANDED_MAP_CONSTANTS.FALLBACK_WIDTH
  );
  const finalHeight = Math.max(
    constrainedHeight,
    EXPANDED_MAP_CONSTANTS.FALLBACK_HEIGHT
  );

  // Center within parent element (not viewport) since map uses position: absolute
  // relative to its parent container
  const x =
    (parentBounds.width - finalWidth) / EXPANDED_MAP_CONSTANTS.DIVISION_FACTOR;
  const y =
    (parentBounds.height - finalHeight) /
    EXPANDED_MAP_CONSTANTS.DIVISION_FACTOR;

  return {
    width: finalWidth,
    height: finalHeight,
    x,
    y,
  };
}

export interface ClickEventOptions {
  expandable?: boolean;
  moveable?: boolean;
  showHeaderReset?: boolean;
}

/**
 * Handle expand/collapse button click
 */
export function handleExpandCollapse(
  event: Event,
  _state: CanvasMapState,
  setState: (updater: (prev: CanvasMapState) => CanvasMapState) => void,
  callbacks: ExpandCollapseCallbacks
): void {
  event.stopPropagation();

  if (callbacks.isExpanded()) {
    // Collapsing - restore to default position, not pre-expand position
    // This ensures consistent behavior regardless of where the map was before expanding
    const originalDefaultPosition = callbacks.calculatePosition();
    const originalDefaultZoom = 1;
    const originalDefaultPanOffset = { x: 0, y: 0 };

    setState((prev) => ({
      ...prev,
      size: { ...DEFAULT_MAP_SIZE },
      position: { ...originalDefaultPosition },
      zoom: originalDefaultZoom,
      panOffset: originalDefaultPanOffset,
    }));

    // Set collapsed state AFTER setState so viewport effect doesn't interfere
    callbacks.setIsExpanded(false);
    callbacks.onUserMoveReset?.(); // Reset user movement tracking
  } else {
    // Expanding - expand to larger view with parent constraints

    // Calculate maximum expanded size based on parent element constraints
    const parentElement = callbacks.getParentElement?.();
    const expandedConstraints = calculateMaxExpandedSize(parentElement);

    const expandedSize = {
      width: expandedConstraints.width,
      height: expandedConstraints.height,
    };
    const centerX = expandedConstraints.x;
    const centerY = expandedConstraints.y;

    // Calculate zoom to fit the map in the expanded container
    const mapContentHeight =
      expandedConstraints.height - MAP_CONSTANTS.HEADER_HEIGHT;
    const fitZoom = Math.min(
      expandedConstraints.width / MAP_CONSTANTS.MAX_WIDTH,
      mapContentHeight / MAP_CONSTANTS.MAX_HEIGHT
    );
    const finalZoom = Math.max(
      EXPANDED_MAP_CONSTANTS.ZOOM_FACTOR,
      Math.min(EXPANDED_MAP_CONSTANTS.ZOOM_MULTIPLIER, fitZoom)
    );

    // CRITICAL: Set expanded state BEFORE setState to prevent viewport effect interference
    callbacks.setIsExpanded(true);
    callbacks.onUserMoveReset?.(); // Reset user movement tracking

    setState((prev) => ({
      ...prev,
      size: expandedSize,
      position: { x: centerX, y: centerY },
      zoom: finalZoom,
      panOffset: { x: 0, y: 0 }, // Reset pan to center
    }));
  }
}

/**
 * Handle undo/reset button click
 */
export function handleUndo(
  event: Event,
  setState: (updater: (prev: CanvasMapState) => CanvasMapState) => void,
  callbacks: UndoCallbacks
): void {
  event.stopPropagation();

  const originalDefaultPosition = callbacks.calculatePosition();
  const originalDefaultZoom = 1.0;
  const originalDefaultPanOffset = { x: 0, y: 0 };

  setState((prev) => ({
    ...prev,
    size: { ...DEFAULT_MAP_SIZE },
    position: { ...originalDefaultPosition },
    zoom: originalDefaultZoom,
    panOffset: originalDefaultPanOffset,
  }));

  // If currently expanded, reset to collapsed state
  callbacks.setIsExpanded(false);

  // Reset user movement tracking
  callbacks.onUserMoveReset?.();

  // Trigger onUndoClick callback if provided
  callbacks.onUndoClick?.();
}

/**
 * Handle home icon click
 */
export function handleHomeIconClick(
  event: MouseEvent,
  callbacks: HomeIconCallbacks
): void {
  event.stopPropagation();
  callbacks.onHomeIconClick?.(callbacks.homeIconName || 'home');
}

/**
 * Handle zoom control actions
 */
export const ZoomController = {
  ZOOM_STEP: EXPANDED_MAP_CONSTANTS.ZOOM_STEP,

  handleZoomIn(
    state: CanvasMapState,
    setState: (updater: (prev: CanvasMapState) => CanvasMapState) => void,
    maxZoom: number
  ): void {
    const newZoom = Math.min(maxZoom, state.zoom + ZoomController.ZOOM_STEP);
    setState((prev) => ({ ...prev, zoom: newZoom }));
  },

  handleZoomOut(
    state: CanvasMapState,
    setState: (updater: (prev: CanvasMapState) => CanvasMapState) => void,
    minZoom: number
  ): void {
    const newZoom = Math.max(minZoom, state.zoom - ZoomController.ZOOM_STEP);

    if (newZoom === minZoom) {
      setState((prev) => ({
        ...prev,
        zoom: newZoom,
        panOffset: { x: 0, y: 0 },
      }));
    } else {
      setState((prev) => ({ ...prev, zoom: newZoom }));
    }
  },

  handleZoomReset(
    setState: (updater: (prev: CanvasMapState) => CanvasMapState) => void,
    minZoom: number
  ): void {
    // Reset to minimum zoom and center the map
    setState((prev) => ({
      ...prev,
      zoom: minZoom,
      panOffset: { x: 0, y: 0 },
    }));
  },
};
